/**
 * The top-bar bell and the panel behind it.
 *
 * The bell used to be a `<span>` showing a number, while `/api/notifications/`,
 * `/api/notifications/{id}/read/` and `/api/notifications/read-all/` were all
 * implemented and tested on the server. A badge with nothing behind it is worse than no
 * badge: it tells someone there is something to read and then refuses to show it.
 *
 * The count polls every minute because it is the only thing visible while the panel is
 * shut. The list itself is fetched when the panel opens and not before — most of the
 * day nobody opens it, and the badge already answers the only question a closed bell
 * can be asked.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  Bot,
  CheckSquare,
  MessageSquare,
  Plug,
  ShieldAlert,
  UserPlus,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState, type JSX } from "react";
import { useAuthStore } from "@/store/auth";
import { useNavigate } from "react-router";

import { coreApi } from "@/api/endpoints";
import { queryKeys } from "@/api/queryKeys";
import type { Notification } from "@/api/types";
import { Button, QueryState } from "@/components/ui";
import { useTranslation } from "@/i18n";
import { formatDate } from "@/lib/format";

import { groupOf, type NotificationGroup } from "./notificationKinds";
import "./NotificationBell.css";

const PANEL_ID = "k-notifications-panel";

const GROUP_ICON: Record<NotificationGroup, LucideIcon> = {
  conversation: MessageSquare,
  lead: UserPlus,
  task: CheckSquare,
  finance: Wallet,
  ai: Bot,
  channel: Plug,
  security: ShieldAlert,
  other: Bell,
};

/** The glyph that says which family of event a notification is, before the title is read. */
function GroupIcon({ group }: { group: NotificationGroup }): JSX.Element {
  const Icon = GROUP_ICON[group];
  return <Icon className="k-bell__icon" aria-hidden="true" size={14} />;
}

export function NotificationBell(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement | null>(null);

  // Notifications belong to an agency. A platform admin with no agency has none to read,
  // and the API refuses the call, so the bell does not ask.
  const hasAgency = useAuthStore((state) => Boolean(state.user?.tenant));
  const unread = useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: () => coreApi.unreadCount(),
    refetchInterval: 60_000,
    enabled: hasAgency,
  });

  // The first page only — 25 rows, the API's page size. The bell is a glance at what
  // is new, not an archive, and the API exposes no page-size parameter to widen it.
  const list = useQuery({
    queryKey: queryKeys.notifications.list(),
    queryFn: () => coreApi.notifications(),
    enabled: open,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });
  };

  const markRead = useMutation({
    mutationFn: (id: number) => coreApi.markNotificationRead(id),
    onSuccess: refresh,
  });

  const markAllRead = useMutation({
    mutationFn: () => coreApi.markAllNotificationsRead(),
    onSuccess: refresh,
  });

  // A dropdown that cannot be dismissed is a trap on a touch screen, and Escape is
  // what a keyboard user reaches for first.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    function onPointerDown(event: PointerEvent): void {
      const root = container.current;
      if (root !== null && event.target instanceof Node && !root.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return (): void => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  const unreadCount = unread.data?.unread ?? 0;
  const criticalCount = unread.data?.critical ?? 0;
  // Critical rows (TZ §2.5) first, in the order the server gave them: a stable sort keeps the
  // newest-first order inside each group.
  const items: readonly Notification[] = [...(list.data?.results ?? [])].sort(
    (left, right) => Number(right.is_critical) - Number(left.is_critical),
  );

  /** Reading a notification is opening it: marking it read on the side would leave the
   *  badge counting items the person has already acted on. */
  function openNotification(item: Notification): void {
    if (!item.is_read) {
      markRead.mutate(item.id);
    }
    if (item.url !== "") {
      setOpen(false);
      void navigate(item.url);
    }
  }

  return (
    <div className="k-bell" ref={container}>
      <button
        type="button"
        className="k-topbar__bell"
        aria-label={t("common.notifications")}
        title={t("common.notifications")}
        aria-expanded={open}
        aria-controls={PANEL_ID}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <Bell strokeWidth={1.5} aria-hidden="true" />
        {unreadCount > 0 ? (
          <span
            className={criticalCount > 0 ? "k-topbar__badge is-critical" : "k-topbar__badge"}
            title={criticalCount > 0 ? t("common.criticalCount", { count: criticalCount }) : undefined}
          >
            {unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="k-bell__panel"
          id={PANEL_ID}
          role="group"
          aria-label={t("common.notifications")}
        >
          <header className="k-bell__head">
            <span className="k-bell__title">{t("common.notifications")}</span>
            <Button
              variant="ghost"
              disabled={unreadCount === 0 || markAllRead.isPending}
              onClick={() => {
                markAllRead.mutate();
              }}
            >
              {t("common.markAllRead")}
            </Button>
          </header>

          <QueryState
            isPending={list.isPending}
            isError={list.isError}
            skeleton={{ shape: "list", rows: 4 }}
            isEmpty={items.length === 0}
            emptyLabel={t("common.noNotifications")}
            onRetry={() => {
              void list.refetch();
            }}
          >
            <ul className="k-bell__list">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`k-bell__item ${item.is_read ? "" : "is-unread"}${item.is_critical ? " is-critical" : ""}`}
                    onClick={() => {
                      openNotification(item);
                    }}
                  >
                    <span className="k-bell__itemTitle">
                      <GroupIcon group={groupOf(item.kind)} />
                      {item.title}
                    </span>
                    {item.is_critical ? (
                      <span className="k-bell__critical">{t("common.criticalLabel")}</span>
                    ) : null}
                    {item.body === "" ? null : (
                      <span className="k-bell__itemBody">{item.body}</span>
                    )}
                    <span className="k-bell__itemDate">{formatDate(item.created_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </QueryState>
        </div>
      ) : null}
    </div>
  );
}
